<template>
  <!--    Contribution Form-->
  <validation-observer
    ref="contributionForm"
    v-slot="{invalid}"
  >
    <am-modal
      :title="`${updateContributionMode ? 'Update Amount Owed' : 'Set Amount Owed'}`"
      :show="contributionModalOpened"
      :show-overlay="formLoading"
      size="md"
      @onClose="closeContributionModal()"
    >
      <b-row class="align-items-start">
        <div class="col-7">
          <am-select
            v-model="selectedContributionId"
            placeholder="All Dues Setup"
            :options="contributionSettings"
            label-name="setting_label"
            value-name="id"
            name="contributions"
            @input="populateContributionForm"
          />
        </div>
        <div class="col-2">
          <am-dropdown
            icon="TrashIcon"
            name="delete_contribution"
          >
            <b-dropdown-item
              id="cd_contribution"
              @click="deleteContribution()"
            >
              Yes
            </b-dropdown-item>
            <b-dropdown-divider />
            <b-dropdown-item>
              No
            </b-dropdown-item>
          </am-dropdown>
        </div>
        <div class="col-2">
          <am-button
            name="contribution_action"
            variant="flat-danger"
            icon="XIcon"
            label=""
            tool-tip-text="Clear Form"
            @click="resetContributionForm()"
          />
        </div>
      </b-row>
      <error-display :error="error" />
      <am-select
        v-model="contribution.transaction_type"
        name="transaction_type"
        label="Transaction Type"
        rules="required"
        :options="transactionTypeOptions"
      />
      <am-select
        v-model="contribution.pricing_level"
        name="pricing_level"
        label="Pricing Level"
        rules="required"
        :options="pricingLevels"
      />
      <div
        v-if="individualContributor"
        class="mb-1"
      >
        <label>Contributor ID</label>
        <div class="d-flex align-items-start contributor-search">
          <b-form-input
            v-model="contributorSearch"
            placeholder="Search contributor by name or ID..."
            class="contributor-search__input"
            @keyup.enter="searchContributors"
          />
          <am-button
            class="contributor-search__btn ml-1"
            icon="SearchIcon"
            label="Search"
            variant="outline-primary"
            :loading="searchingContributor"
            @click="searchContributors"
          />
        </div>
        <div class="mt-1">
          <am-select
            v-model="contribution.contributor_id"
            name="contributor"
            label="Select contributor"
            rules="required"
            placeholder="Select a contributor from search results"
            label-name="label"
            value-name="contributor_id"
            :options="contributorOptions"
            :disabled="!contributorOptions.length"
            @input="onContributorSelected"
          />
        </div>
        <small
          v-if="contributorSearchDone && !contributorOptions.length"
          class="text-muted"
        >
          No contributors found. Try another search.
        </small>
        <div
          v-if="contributor"
          class="d-flex align-items-center mt-1"
        >
          <am-profile-photo
            size="30"
            :photo="contributor.photo"
          />
          <div style="margin-right: 5px;" />
          <b-badge variant="light-primary">
            {{ contributor.name }}
          </b-badge>
          <am-button
            icon-only
            variant="flat-danger"
            icon="XIcon"
            @click="clearContributor()"
          />
        </div>
      </div>
      <am-input
        v-model="contribution.amount"
        name="amount"
        :label="`Amount (${currencySymbol})`"
        rules="required|numeric"
      />
      <am-select
        v-model="contribution.contribution_season"
        name="contribution season"
        label="Contribution Season"
        rules="required"
        :options="seasons"
      />
      <am-date-picker
        id="active_from"
        v-model="contribution.active_from"
        name="active_from"
        label="Active From"
        rules="required"
      />
      <am-date-picker
        id="active_to"
        v-model="contribution.active_to"
        name="active_to"
        label="Active To"
        rules="required"
      />
      <div class="mt-2" />
      <b-form-checkbox>
        Alert Contributors (SMS)
      </b-form-checkbox>
      <template slot="modal-actions">
        <b-row class="float-right">
          <am-button
            name="cancel"
            variant="flat-danger"
            icon="XIcon"
            class="mr-1"
            label="Cancel"
            @click="closeContributionModal()"
          />
          <am-button
            name="save_contribution"
            :label="updateContributionMode ? 'Update Amount Owed' : 'Set Amount Owed'"
            :disabled="invalid"
            :loading="contributionLoading"
            loading-text="Loading..."
            @click="updateContributionMode?updateContribution():createContribution()"
          />
        </b-row>
      </template>
    </am-modal>
  </validation-observer>
</template>

<script>
import {
  BRow, BDropdownItem, BDropdownDivider, BFormCheckbox, BBadge, BFormInput,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import logData from '@/libs/log'
import AmInput from '@/farmbusiness/components/AmInput.vue'
import AmSelect from '@/farmbusiness/components/AmSelect.vue'
import AmButton from '@/farmbusiness/components/AmButton.vue'
import AmDropdown from '@/farmbusiness/components/AmDropdown.vue'
import AmModal from '@/farmbusiness/components/AmModal.vue'
import showToast from '@/farmbusiness/mixins/showToast'
import ErrorDisplay from '@/farmbusiness/components/AmErrorDisplay.vue'
import AmDatePicker from '@/farmbusiness/components/AmDatePicker.vue'
import AmProfilePhoto from '@/farmbusiness/components/AmProfilePhoto.vue'
import { formatContributionLabel } from '@/farmbusiness/utils/helperFunctions'

const cont = {
  id: null,
  transaction_type: null,
  pricing_level: null,
  amount: null,
  contribution_season: null,
  active_from: null,
  active_to: null,
  contributor_id: null,
  alert: false,
  is_member_contribution: false,
}
export default {
  name: 'ContributionSettingSetup',
  components: {
    AmProfilePhoto,
    AmDatePicker,
    ErrorDisplay,
    AmModal,
    AmDropdown,
    AmButton,
    AmSelect,
    AmInput,
    ValidationObserver,
    BRow,
    BDropdownItem,
    BDropdownDivider,
    BFormCheckbox,
    BBadge,
    BFormInput,
  },
  mixins: [showToast],
  props: {
    modalOpened: {
      type: Boolean,
      default: false,
    },
    id: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      error: {},
      contributionModalOpened: false,
      updateContributionMode: false,
      contributionLoading: false,
      formLoading: false,
      hydrating: false,
      selectedContributionId: null,
      contribution: { ...cont },
      transactionTypes: [
        { label: 'Member Dues', value: 'Member Dues' },
        { label: 'Union Dues', value: 'Organization Dues' },
        { label: 'Society Registration Fee', value: 'Organization Registration Fee' },
      ],
      seasons: [],
      contributorSearch: '',
      contributorOptions: [],
      searchingContributor: false,
      contributorSearchDone: false,
    }
  },
  computed: {
    transactionTypeOptions() {
      const types = [...this.transactionTypes]
      if (this.contribution.transaction_type === 'Pension Contribution'
        && !types.some(t => t.value === 'Pension Contribution')) {
        types.splice(2, 0, { label: 'Pension Contribution', value: 'Pension Contribution' })
      }
      return types
    },
    memberContribution() {
      return this.transactionType?.includes('Member') || this.transactionType?.includes('Pension')
    },
    individualContributor() {
      return this.contribution.pricing_level?.includes('Individual')
    },
    transactionType() {
      return this.contribution.transaction_type
    },
    pricingLevels() {
      if (this.contribution.transaction_type) {
        if (this.contribution.transaction_type === 'Member Dues'
            || this.contribution.transaction_type === 'Pension Contribution') {
          return ['All Members', 'Individual Member']
        }
        return [{ label: 'All Societies', value: 'All Organizations' }, { label: 'Individual Society', value: 'Individual Organization' }]
      }
      return []
    },
    currencySymbol() {
      return 'GHS'
    },
    selectedContribution() {
      const contribution = this.contributionSettings.find(o => o.id === this.selectedContributionId)
      if (!contribution) {
        return null
      }
      return {
        ...contribution,
      }
    },
    contributionSettings() {
      return (this.$store.getters['contributionSettings/contributionSettings'] || []).map(item => ({
        ...item,
        setting_label: this.formatContributionLabel(item.setting_label),
      }))
    },
    contributor() {
      return this.$store.getters['contributionSettings/contributor']
    },
  },
  watch: {
    modalOpened(opened) {
      this.contributionModalOpened = opened
    },
    id(id) {
      this.selectedContributionId = id
    },
    transactionType(type, oldType) {
      if (this.hydrating || oldType == null) {
        return
      }
      if (oldType !== type) {
        this.contribution.pricing_level = null
        this.clearContributor()
      }
    },
    individualContributor(isIndividual) {
      if (this.hydrating) {
        return
      }
      if (!isIndividual) {
        this.clearContributor()
      }
    },
  },
  mounted() {
    this.createSeasons()
  },
  methods: {
    formatContributionLabel,
    clearContributor() {
      this.contribution.contributor_id = null
      this.contributorSearch = ''
      this.contributorOptions = []
      this.contributorSearchDone = false
      this.$store.commit('contributionSettings/setContributor', null)
    },
    createSeasons() {
      const yearLimit = new Date().getUTCFullYear()
      for (let i = 2022; i <= yearLimit + 2; i++) {
        this.seasons.push(`${i}/${i + 1}`)
      }
    },
    resetContributionForm() {
      this.contribution = { ...cont }
      this.selectedContributionId = null
      this.updateContributionMode = false
      this.clearContributor()
      this.$refs.contributionForm.reset()
    },
    populateContributionForm() {
      if (this.selectedContribution) {
        this.hydrating = true
        this.updateContributionMode = true
        this.contribution = { ...this.selectedContribution, alert: false }
        this.$nextTick(() => {
          if (this.individualContributor && this.contribution.contributor_id) {
            this.contributorOptions = [{
              contributor_id: this.contribution.contributor_id,
              label: this.contribution.contributor_id,
            }]
            this.loadContributorDetails(this.contribution.contributor_id)
          }
          this.hydrating = false
        })
      } else {
        this.resetContributionForm()
      }
    },
    closeContributionModal() {
      this.contributionModalOpened = false
      this.$emit('modalClosed')
    },
    searchContributors() {
      const term = (this.contributorSearch || '').trim()
      if (!term) {
        this.showError('Enter a contributor name or ID to search')
        return
      }
      this.searchingContributor = true
      this.contributorSearchDone = false
      const action = this.memberContribution
        ? 'contributionSettings/searchMembers'
        : 'contributionSettings/searchOrganizations'
      this.$store.dispatch(action, { search: term, limit: 8 })
        .then(results => {
          this.contributorOptions = results || []
          this.contributorSearchDone = true
          if (this.contribution.contributor_id
            && !this.contributorOptions.some(o => String(o.contributor_id) === String(this.contribution.contributor_id))) {
            this.contribution.contributor_id = null
            this.$store.commit('contributionSettings/setContributor', null)
          }
          if (this.contributorOptions.length === 1) {
            this.contribution.contributor_id = this.contributorOptions[0].contributor_id
            this.onContributorSelected(this.contribution.contributor_id)
          }
        })
        .catch(() => {
          this.showError('Failed to search contributors')
        })
        .finally(() => {
          this.searchingContributor = false
        })
    },
    onContributorSelected(id) {
      if (!id) {
        this.$store.commit('contributionSettings/setContributor', null)
        return
      }
      this.loadContributorDetails(id)
    },
    loadContributorDetails(id) {
      this.formLoading = true
      const action = this.memberContribution
        ? 'contributionSettings/getMember'
        : 'contributionSettings/getOrganization'
      this.$store.dispatch(action, id)
        .catch(err => {
          this.error = err
        })
        .finally(() => {
          this.formLoading = false
        })
    },
    createContribution() {
      this.error = {}
      this.contributionLoading = true
      if (!this.individualContributor) {
        this.contribution.contributor_id = null
      }
      this.contribution.is_member_contribution = this.memberContribution
      this.$store.dispatch('contributionSettings/createContribution', this.contribution)
        .then(() => {
          this.showSuccess('Created contribution successfully')
          this.resetContributionForm()
        })
        .catch(err => {
          logData(err)
          this.error = err
          this.showError('Failed to create contribution')
        }).finally(() => {
          this.contributionLoading = false
        })
    },
    updateContribution() {
      this.error = {}
      if (!this.contribution.id) {
        return
      }
      if (!this.individualContributor) {
        this.contribution.contributor_id = null
      }
      this.contribution.is_member_contribution = this.memberContribution
      this.contributionLoading = true
      this.$store.dispatch('contributionSettings/updateContribution', this.contribution)
        .then(() => {
          this.showSuccess('Updated contribution successfully')
        })
        .catch(err => {
          logData(err)
          this.error = err
          this.showError('Failed to update contribution')
        }).finally(() => {
          this.contributionLoading = false
        })
    },
    deleteContribution() {
      if (!this.selectedContributionId) {
        this.showInfo('Please select a contribution')
        return
      }
      this.formLoading = true
      this.$store.dispatch('contributionSettings/deleteContribution', this.selectedContributionId)
        .then(() => {
          this.showSuccess('Deleted Successfully')
          this.resetContributionForm()
        })
        .catch(() => {
          this.showError('Failed to delete contribution')
        }).finally(() => {
          this.formLoading = false
        })
    },
  },
}
</script>

<style scoped>
.contributor-search {
  width: 100%;
}
.contributor-search__input {
  flex: 1;
}
.contributor-search__btn {
  flex-shrink: 0;
}
.contributor-search__btn >>> .btn,
.contributor-search__btn >>> div {
  height: 100%;
}
</style>
